import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {
    IPixiAppCreateOptions, IPixiAppCreationResult, Container, Graphics, ITextStyle, Text, ParticleContainer,
    EmitterConfigV1, EmitterConfigV2, EmitterConfigV3, Emitter
} from "../pixi-app.service.interface";
import {PixiAsset} from "../pixi-assets-manager/asset/pixi-asset";
import {IPixiSpineRawAsset} from "../pixi-assets-manager/asset/pixi-spine-raw-asset.interface";
import {PixiAssetsManager} from "../pixi-assets-manager/pixi-assets-manager";
import {IPixiLibWrapper} from "./pixi-lib-wrapper.interface";
import {PixiSpineResource} from "../resources/pixi-spine-resource";
import {PixiSpriteResource} from "../resources/pixi-sprite-resource";

const LOAD_ASSET_TIMEOUT = 30000;

export class PixiLibWrapper implements IPixiLibWrapper {
    /** ///////////////////////////////////
     *  //////// SINGLETON FACTORY ////////
     */ ///////////////////////////////////
    private static _selfInstancePromise: Promise<IPixiLibWrapper> | null;

    private static async loadPixi() {
        const PIXI = await import("pixi.js-legacy");
        const PixiSpine = await import("pixi-spine");
        const PixiParticlesEmitter = await import("@pixi/particle-emitter");
        return {PIXI, PixiSpine, PixiParticlesEmitter};
    }
    // TODO: mitigate the services dependency
    public static getInstancePromise(services: IServiceFactoryExtended): Promise<IPixiLibWrapper> {
        if (!PixiLibWrapper._selfInstancePromise) {
            PixiLibWrapper._selfInstancePromise = PixiLibWrapper.loadPixi().then(({PIXI, PixiSpine, PixiParticlesEmitter}) => {
                return new PixiLibWrapper(PIXI, PixiSpine, PixiParticlesEmitter, services);
            });
        }
        return PixiLibWrapper._selfInstancePromise;
    }


    /** ////////////////////////////////////////////////
     *  //////// SINGLETON PRIVATE CONSTRUCTOR /////////
     */ ////////////////////////////////////////////////
    private constructor(
        private PIXI: typeof import("pixi.js-legacy"),
        private PixiSpine: typeof import("pixi-spine"),
        private PixiParticlesEmitter: typeof import("@pixi/particle-emitter"),
        private services: IServiceFactoryExtended
    ) {
        this._pixiAssetsManager = new PixiAssetsManager(this);
    }


    /** //////////////////////////////////////////
     *  //////// INSTANCE IMPLEMENTATION /////////
     */ //////////////////////////////////////////
    private _pixiAppCreatorWithWebGlManager: (options: IPixiAppCreateOptions) => IPixiAppCreationResult;
    private _pixiAssetsManager: PixiAssetsManager;

    public createPixiApplication(options: IPixiAppCreateOptions): IPixiAppCreationResult {
        // In pixi 7, forcing 2D canvas context instead of Web GL makes the animation squeezed, it looks bad.
        // I am making this hack now and I will investigate if we can make a fix.
        // If I cannot, I will remove this option
        // TODO: Address this hack by debugging, googling and investigating possible solutions
        // START OF HACK
        if (options.force2DCanvasContext) { options.force2DCanvasContext = false; }
        // END OF HACK
        if (!this._pixiAppCreatorWithWebGlManager) {
            this._pixiAppCreatorWithWebGlManager = this.services.webGlContextManager.makeCreator<IPixiAppCreateOptions, IPixiAppCreationResult>(this._createPixiApplicationInternal);
        }
        if (options.force2DCanvasContext) {
            return this._createPixiApplicationInternal(options)
        }
        return this._pixiAppCreatorWithWebGlManager(options);
    }

    
    public loadAssets(assetsUrls: Record<string, string>): Promise<Record<string, PixiAsset>> {
        return this._pixiAssetsManager.load(assetsUrls);
    }


    /**
     * This function actually creates the pixi application but we don't call it directly, we use a decorator function that might or might not wrap the result
     * into a webGLManager
     * Is important that this function is an arrow function because it's reference is passed to webGLManagerService
     * and if not called this way this will be undefined 
     */
    private _createPixiApplicationInternal = (options: IPixiAppCreateOptions): IPixiAppCreationResult => {
        let app = new this.PIXI.Application<HTMLCanvasElement>({
            backgroundAlpha: 0,
            backgroundColor: options.backgroundColor,
            width: options.width,
            height: options.height,
            antialias: true,
            forceCanvas: !!options.force2DCanvasContext
        });
    
        const dispose = () => {
            try {
                app.view.removeEventListener("webglcontextlost", contextLostHandler, false);
                app.stop();
                // In pixi 6 this was called with both parameters on true because pixi 6 loads assets for each pixi app independently.
                // From pixi 7, the assets are loaded in a global pool and if I destroy them with the second parameter on true they are not re-created, don't know why
                // Also, when destroying textures, children and baseTextures it also destroys all other pixiApps that share the same resources
                // I also get this warning in console: [Assets] A BaseTexture managed by Assets was destroyed instead of unloaded! Use Assets.unload() instead of destroying the BaseTexture.
                // So I don't destroy them here but I have the assets manager that takes care about that
                app.destroy(true, false);
                app = undefined;
            } catch (err) {
                console.error('Failed to dispose pixi application', err);
            }
    
        };
    
        const contextLostHandler = (_e: WebGLContextEvent) => {
            if (options.onContextLost) {
                options.onContextLost();
            }
            if (options.selfDestroyOnContextLost !== false) {
                dispose();
            }
        };
    
        app.view.addEventListener("webglcontextlost", contextLostHandler, false);
        if (options.id) {
            app.view.id = options.id;
        }

        return {app, canvas: app.view, dispose};
    }

    async loadAsset(src: string) {
        // PIXI.Assets.load has 3 signatures
        // 1. When passing a string (the url), the promise resolves with one resource
        // 2. When passing an array of urls, the promise resolves with an object where the urls are keys and the resources are values
        // 3. When passing an array of objects {name: string; src: string}, the promise resolves with an object where the names are keys and the resources are values.
        // However, the name must be UNIQUE IN WHOLE ORBIT APP otherwise it will return a resource previously loaded with the same name.
        // Sometimes, when there are problems in spine animation we get this warning in the console followed by this error but this promise remains in pending forever:
        // Warning: [Assets] https://localhost:7070/gamesImages/( could not be loaded as we don't know how to parse it, ensure the correct parser has been added
        // Error: Uncaught (in promise) TypeError: Cannot read properties of null (reading 'baseTexture')
        // That is why we use signature 1 with a timeout. For multiple resources we just call this method multiple times
        return Promise.race([
            this.PIXI.Assets.load(src),
            new Promise<never>((res, rej) => setTimeout(() => {
                rej(new Error("Asset load timed out: " + src));
            }, LOAD_ASSET_TIMEOUT))
        ]);
    }
    
    async unloadAsset(src: string) {
        this.PIXI.Assets.unload(src);
    }
    
    private checkRawAssetIsSpineData(asset: PixiAsset): asset is PixiAsset<IPixiSpineRawAsset> {
        if (asset.rawAsset.spineData) { return true; } else { return false; }
    }
    createPixiSpineResource(asset: PixiAsset) {
        if (!this.checkRawAssetIsSpineData(asset)) {
            throw new Error("raw asset is not a spine asset");
        }
        const spineAnimation = new this.PixiSpine.Spine(asset.rawAsset.spineData);
        return new PixiSpineResource(asset, spineAnimation);
    }

    createContainer(): Container {
        return new this.PIXI.Container();
    }

    createGraphics(): Graphics {
        return new this.PIXI.Graphics();
    }

    createPixiSpriteResource(asset: PixiAsset): PixiSpriteResource {
        // TODO: Check integrity/compatibility of the asset if possible
        const sprite = new this.PIXI.Sprite(asset.rawAsset);
        return new PixiSpriteResource(asset, sprite);
    }

    createText(text: string, style?: Partial<ITextStyle>): Text {
        return new this.PIXI.Text(text, new this.PIXI.TextStyle(style));
    }

    createParticleContainer(): ParticleContainer {
        return new this.PIXI.ParticleContainer();
    }

    particleContainerUpgradeConfig(config: EmitterConfigV2 | EmitterConfigV1, art: any): EmitterConfigV3 {
        return this.PixiParticlesEmitter.upgradeConfig(config, art);
    }
    createEmitter(particleParent: Container, config: EmitterConfigV3): Emitter {
        return new this.PixiParticlesEmitter.Emitter(particleParent, config);
    }
}
